import { motion } from "framer-motion";
import { Download, Mail } from "lucide-react";
import { COMPANY } from "@/constants/company";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

const PRODUCTS = [
  {
    name: "Unixx 5W-40 Professional Formula",
    tag: "Fully Synthetic",
    image: "/assets/unixx-5w40.jpg",
    badges: ["API SN/SM/CF", "ACEA A3/B3/B4", "Porsche A40", "BMW LL-01", "MB 229.3", "VW 502 00/505 00"],
    desc: "High viscosity index synthetic base oil fortified with premium additives — unsurpassed wear protection, active deposit and sludge cleaning, and exceptional stability under high temperatures and extended drain intervals. Suitable for all new and high-performance cars.",
    packs: "4 L & 1 L",
    pds: "/specs/unixx-pds-5w40.pdf",
    specs: [
      ["Density @ 30°C, g/cm³", "0.8456", "ASTM D4052"],
      ["Kinematic viscosity @ 40°C, mm²/s", "87.58", "ASTM D445"],
      ["Kinematic viscosity @ 100°C, mm²/s", "14.42", "ASTM D445"],
      ["Viscosity index", "172", "ASTM D2270"],
      ["Flash point (COC), °C", "250", "ASTM D92"],
      ["Pour point, °C", "-27", "ASTM D5950/D6892"],
      ["Total base number, mgKOH/g", "9.18", "ASTM D2896"],
    ],
  },
  {
    name: "Unixx 10W-40 Performance Formula",
    tag: "Synthetic Blend",
    image: "/assets/unixx-10w40.jpg",
    badges: ["API SN/CF"],
    desc: "Hydro-clear base oil with high thermal stability and superior resistance to oil degradation. Excellent oxidation stability extends oil life, while the additive package delivers wear and corrosion protection and keeps engines clean and responsive. Suitable for all modern cars requiring API SN/CF.",
    packs: "4 L & 1 L",
    pds: "/specs/unixx-pds-10w40.pdf",
    specs: [
      ["Density @ 30°C, g/cm³", "0.8574", "ASTM D4052"],
      ["Kinematic viscosity @ 40°C, mm²/s", "101.2", "ASTM D445"],
      ["Kinematic viscosity @ 100°C, mm²/s", "14.71", "ASTM D445"],
      ["Viscosity index", "151", "ASTM D2270"],
      ["Flash point (COC), °C", "240", "ASTM D92"],
      ["Pour point, °C", "-30", "ASTM D5950/D6892"],
      ["Total base number, mgKOH/g", "7.43", "ASTM D2896"],
    ],
  },
  {
    name: "Unixx 15W-40 Advanced Formula",
    tag: "Mineral Diesel",
    image: "/assets/unixx-15w40.jpg",
    badges: ["API CI-4/CH-4/SL/SJ", "ACEA E7-08", "JASO DH-1", "CAT ECF-1a", "Global DHD-1", "Mack EO-N", "MAN M3275-1", "MB 228.3", "MTU Cat 2", "Renault RLD-2", "Volvo VDS-3"],
    desc: "High-performance diesel engine oil for virtually all major American and European commercial vehicles, including EGR-equipped engines. Advanced additives and high-quality base stock deliver excellent soot and contaminant control, strong resistance to oil degradation and superior wear protection for longer engine life.",
    packs: "6 L & 1 L",
    pds: "/specs/unixx-pds-15w40.pdf",
    specs: [
      ["Density @ 15°C, g/cm³", "0.8776", "ASTM D4052"],
      ["Density @ 30°C, g/cm³", "0.8682", "ASTM D4052"],
      ["Kinematic viscosity @ 40°C, mm²/s", "111.4", "ASTM D445"],
      ["Kinematic viscosity @ 100°C, mm²/s", "14.54", "ASTM D445"],
      ["Viscosity index", "134", "ASTM D2270"],
      ["Flash point (COC), °C", "228", "ASTM D92"],
      ["Pour point, °C", "-39", "ASTM D5950/6892/6749"],
      ["Total base number, mgKOH/g", "10.91", "ASTM D2896"],
      ["Sulfated ash, wt%", "1.41", "ASTM D874"],
    ],
  },
];

export default function UnixxProducts() {
  return (
    <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="unixx-products-section">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <motion.p {...reveal()} className="font-mono text-xs uppercase tracking-[0.35em] text-brand">
          Featured Brand
        </motion.p>
        <motion.h2 {...reveal(0.05)} className="mt-4 text-2xl font-bold text-white sm:text-3xl">
          Unixx engine oils
        </motion.h2>
        <motion.p {...reveal(0.1)} className="mt-4 max-w-2xl text-base text-slate-300">
          Our featured finished-lubricant range, blended to API and OEM standards and packed for retail and
          fleet distribution.
        </motion.p>

        <div className="mt-14 space-y-10">
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.name}
              {...reveal(0.05)}
              className={`grid items-center gap-10 rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50 md:p-10 lg:grid-cols-2 ${
                i % 2 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
              data-testid={`unixx-product-${i}`}
            >
              <div className="clip-frame overflow-hidden">
                <motion.img
                  src={p.image}
                  alt={`${p.name} — ${p.tag} engine oil`}
                  className="h-80 w-full object-cover"
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: EASE }}
                />
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-2xl font-bold text-white">{p.name}</h3>
                  <span className="rounded-full border border-brand/40 bg-brand/10 px-3 py-1 font-mono text-xs text-brand">{p.tag}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.badges.map((b) => (
                    <span key={b} className="rounded-full border border-navy-600 px-3 py-1 font-mono text-xs text-slate-300">{b}</span>
                  ))}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                <p className="mt-3 font-mono text-xs text-slate-500">Pack sizes: {p.packs}</p>

                {p.specs && (
                  <div className="mt-6 overflow-x-auto rounded-md border border-navy-600" data-testid={`unixx-spec-table-${i}`}>
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-navy-600 bg-navy-700 uppercase tracking-wider text-slate-400">
                          <th className="px-4 py-3 font-medium">Property</th>
                          <th className="px-4 py-3 font-medium">Typical value</th>
                          <th className="px-4 py-3 font-medium">Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.specs.map(([prop, val, method]) => (
                          <tr key={prop} className="border-b border-navy-600/50 last:border-0">
                            <td className="px-4 py-2.5 text-slate-300">{prop}</td>
                            <td className="px-4 py-2.5 font-mono text-brand">{val}</td>
                            <td className="px-4 py-2.5 font-mono text-slate-500">{method}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-6">
                  {p.pds ? (
                    <a
                      href={p.pds}
                      download
                      data-testid={`unixx-pds-download-${i}`}
                      className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-6 py-2.5 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
                    >
                      <Download className="h-4 w-4" /> Download product data sheet
                    </a>
                  ) : (
                    <a
                      href={`mailto:${COMPANY.email}?subject=PDS request — Unixx 15W-40 Advanced Formula`}
                      data-testid={`unixx-pds-request-${i}`}
                      className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-6 py-2.5 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
                    >
                      <Mail className="h-4 w-4" /> Request product data sheet
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
